import {
    fetchBaseQuery,
    type BaseQueryApi,
    type BaseQueryFn,
    type FetchArgs,
    type FetchBaseQueryError,
    type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { accessToken } from '../../../configuration/tokenHandling/accessToken';
import { config } from '../../../config';

type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];

export interface JsonObject {
    [key: string]: JsonValue;
}

export type ApiServiceName = 'driverAdmin' | 'userAdmin' | 'tags';

export interface ApiError {
    status: number;
    detail: string | null;
}

type QueryParamValue = string | number | boolean | null | undefined;

export interface ServiceQueryArgs {
    service?: ApiServiceName;
    path?: string;
    absoluteUrl?: string;
    method?: 'GET' | 'PUT' | 'POST' | 'DELETE';
    body?: object;
    headers?: Record<string, string>;
    params?: Record<string, QueryParamValue>;
}

const rawBaseQuery = fetchBaseQuery({
    baseUrl: '',
    responseHandler: async response => {
        const text = await response.text();
        if (!text) {
            return null;
        }

        try {
            return JSON.parse(text) as JsonValue;
        } catch {
            return text;
        }
    },
});

const backendUrlByService: Record<ApiServiceName, string | undefined> = {
    driverAdmin: config.backend.DRIVERADMIN_SERVICE,
    userAdmin: config.backend.USERADMIN_SERVICE,
    tags: config.backend.TAGS_SERVICE,
};

const ensureSlashPrefixedPath = (path: string) => (path.startsWith('/') ? path : `/${path}`);

const sanitizeParams = (params?: Record<string, QueryParamValue>) => {
    if (!params) {
        return undefined;
    }

    const filteredEntries = Object.entries(params).filter((entry): entry is [string, string | number | boolean] => {
        const value = entry[1];
        return value !== undefined && value !== null;
    });

    return Object.fromEntries(filteredEntries);
};

const resolveUrl = (args: ServiceQueryArgs) => {
    if (args.absoluteUrl) {
        return args.absoluteUrl;
    }

    if (!args.service) {
        throw new Error('service is required when absoluteUrl is not provided');
    }

    const serviceBaseUrl = backendUrlByService[args.service];
    if (!serviceBaseUrl) {
        throw new Error(`Base URL is missing for service "${args.service}"`);
    }

    if (!args.path) {
        throw new Error(`path is required for service "${args.service}"`);
    }

    return `${serviceBaseUrl}${ensureSlashPrefixedPath(args.path)}`;
};

const buildHeaders = (args: ServiceQueryArgs) => {
    const headers = new Headers(args.headers);
    const bearerToken = accessToken.getAccessToken();

    if (bearerToken) {
        headers.set('Authorization', `Bearer ${bearerToken}`);
    }

    if (args.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    return headers;
};

const buildFetchArgs = (args: ServiceQueryArgs): FetchArgs => ({
    url: resolveUrl(args),
    method: args.method ?? 'GET',
    body: args.body,
    headers: buildHeaders(args),
    params: sanitizeParams(args.params),
});

const getDetail = (data: FetchBaseQueryError['data']): string | null => {
    if (data && typeof data === 'object' && !Array.isArray(data) && 'detail' in data) {
        const detail = data.detail;
        return typeof detail === 'string' ? detail : null;
    }

    return null;
};

const toApiError = (error: FetchBaseQueryError): ApiError => {
    if (typeof error.status === 'number') {
        return {
            status: error.status,
            detail: getDetail(error.data),
        };
    }

    switch (error.status) {
        case 'PARSING_ERROR':
            return {
                status: error.originalStatus,
                detail: error.error,
            };
        case 'FETCH_ERROR':
        case 'TIMEOUT_ERROR':
        case 'CUSTOM_ERROR':
            return {
                status: 0,
                detail: error.error,
            };
    }
};

export const isApiError = (error: object | null | undefined): error is ApiError =>
    Boolean(error && 'status' in error && 'detail' in error);

export const serviceBaseQuery: BaseQueryFn<
    ServiceQueryArgs,
    JsonValue | null,
    ApiError,
    {},
    FetchBaseQueryMeta
> = async (args: ServiceQueryArgs, api: BaseQueryApi, extraOptions: {}) => {
    const result = await rawBaseQuery(buildFetchArgs(args), api, extraOptions);

    if (result.error) {
        return {
            error: toApiError(result.error),
            meta: result.meta,
        };
    }

    return {
        data: result.data as JsonValue | null,
        meta: result.meta,
    };
};
